import { ChatPane } from "components/ChatPane/ChatPane";
import { Button } from "components/DesignSystem/Button/Button";
import Slider from "components/DesignSystem/Slider/Slider";
import { ConditionalLink } from "components/conditionalLink";
import ToolTip from "components/design/toolTip";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { ChatIconFilled } from "components/icons/ChatIconFilled";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { CHAT_CLICKED_FROM_BUBBLE } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useMessageCount } from "hooks/useMessageCount";
import { useWindowSize } from "hooks/useWindowSize";
import { motion } from "framer-motion";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  closeFloatingChat,
  openFloatingChat,
  removeToOpenChatUrl,
} from "store/slices/chat";
import { RootState } from "store/store";

// Overlay covers ~70% of the screen so the channel list and thread both fit
const CHAT_OVERLAY_SCREEN_RATIO = 0.7;
const MIN_CHAT_OVERLAY_WIDTH = 720;
const MAX_CHAT_OVERLAY_WIDTH = 1440;

export const FloatingChat = ({
  position: { bottom, right } = { bottom: 32, right: 32 },
  hideOpenedIcon,
  filter,
  defaultChannelId,
  className = "",
  overlayClassName = "",
  open: openFromProps,
  onOpenChange: onOpenChangeFromProps,
}: {
  position?: { right?: number; bottom?: number };
  hideOpenedIcon?: boolean;
  filter?: object;
  defaultChannelId?: string | null;
  className?: string;
  overlayClassName?: string;
  open?: boolean;
  onOpenChange?: (v: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { isOpen, toOpenChatId } = useSelector(
    (state: RootState) => state.chat
  );
  const { messages = 0 } = useMessageCount() || {};
  const isSmallScreen = useIsSmallScreen();
  const { width: windowWidth } = useWindowSize();
  const formattedCount = messages > 99 ? "99+" : messages;
  const [channelId, setChannelId] = useState<string | null>(
    toOpenChatId || null
  );
  const dragBoundsRef = useRef<HTMLDivElement>(null);
  const hasDraggedRef = useRef(false);

  useEffect(() => {
    setChannelId(toOpenChatId);
  }, [toOpenChatId]);

  useEffect(() => {
    if (defaultChannelId) setChannelId(defaultChannelId);
  }, [defaultChannelId]);

  const sliderWidth = useMemo(() => {
    const viewportWidth = windowWidth || window.innerWidth;

    if (isSmallScreen) return viewportWidth;

    return Math.min(
      Math.max(
        Math.round(viewportWidth * CHAT_OVERLAY_SCREEN_RATIO),
        MIN_CHAT_OVERLAY_WIDTH
      ),
      MAX_CHAT_OVERLAY_WIDTH,
      viewportWidth
    );
  }, [windowWidth, isSmallScreen]);

  const onOpenChange = (open: boolean) => {
    if (open) {
      dispatch(openFloatingChat());
      trackEvent(CHAT_CLICKED_FROM_BUBBLE);
    } else dispatch(closeFloatingChat());
  };

  const handleDragStart = () => {
    hasDraggedRef.current = true;
  };

  // A drag ends with a click on the bubble; swallow it so dragging doesn't open the chat
  const handleBubbleClickCapture = (e: MouseEvent) => {
    if (!hasDraggedRef.current) return;

    hasDraggedRef.current = false;
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Slider.Root
      open={openFromProps || isOpen}
      onOpenChange={onOpenChangeFromProps || onOpenChange}
      modal={false}
    >
      <div
        ref={dragBoundsRef}
        className="t-pointer-events-none t-fixed t-inset-0"
      />
      <motion.div
        drag
        dragConstraints={dragBoundsRef}
        dragElastic={0}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onClickCapture={handleBubbleClickCapture}
        style={{ right, bottom }}
        className="t-fixed t-z-[1001] t-touch-none"
      >
        {!hideOpenedIcon ? (
          <div className="t-relative">
            <Slider.Trigger asChild>
              <button className="t-border-0 t-size-12 sm:t-size-10 t-rounded-full t-bg-purple hover:t-bg-purple-80 t-drop-shadow-bubble">
                <ChatIconFilled className="t-size-5 sm:t-size-4 t-text-white -t-translate-y-[1px]" />
              </button>
            </Slider.Trigger>
            {!isOpen && messages > 0 && (
              <span className="t-pointer-events-none t-absolute t-bottom-full t-left-full t-flex t-h-6 t-w-6 -t-translate-x-[80%] t-translate-y-[70%] t-items-center t-justify-center t-rounded-full t-bg-red-50 t-text-body-sm t-text-neutral-0 sm:t-h-5 sm:t-w-5 sm:t-text-caption">
                {formattedCount}
              </span>
            )}
          </div>
        ) : null}
      </motion.div>

      <Slider.Content
        useCustomOverlay
        withOverlay={true}
        width={sliderWidth}
        className={className}
        overlayClassName={overlayClassName}
      >
        <div
          tabIndex={0}
          className="t-sr-only t-outline-none"
          aria-hidden="true"
        />
        <Slider.Header>
          <div className="t-flex t-items-center t-gap-3">
            {channelId && isSmallScreen && (
              <Button
                customType="ghost_icon"
                type="button"
                size="small"
                onClick={(e) => dispatch(removeToOpenChatUrl())}
              >
                <ArrowLeft className="t-size-5 t-text-text-30" />
              </Button>
            )}
            <Slider.Title>Chat</Slider.Title>
          </div>
          <div className="t-flex t-gap-2 t-items-center">
            <ToolTip text="Open full chat">
              <ConditionalLink to="/chat" className="!t-text-text-30">
                <Button
                  type="button"
                  customType="ghost_icon"
                  size="small"
                  className="!focus-visible:t-ring-0"
                >
                  <SmallArrowUpRight className="t-size-3" />
                </Button>
              </ConditionalLink>
            </ToolTip>
            <Slider.Close />
          </div>
        </Slider.Header>
        <ChatPane
          channelId={channelId}
          setChannelId={setChannelId}
          actionsAvailable={true}
          canGoback={isSmallScreen}
          customBackButton={<></>}
          filter={filter}
          splitView
        />
      </Slider.Content>
    </Slider.Root>
  );
};
